/**
 * Overworld biomes: the vanilla (1.18+) multi-noise selection tables and a
 * descriptor per biome (colours, surface, trees, flora).
 *
 * Selection mirrors OverworldBiomeBuilder: temperature x humidity pick from
 * the middle / plateau / shattered tables, continentalness x erosion x
 * weirdness-slice pick which table applies. Pure data + pure functions.
 */

import {
  ALLIUM_ID, AZURE_BLUET_ID, BAMBOO_ID, BLUE_ORCHID_ID, BROWN_MUSHROOM_ID, CORNFLOWER_ID,
  DANDELION_ID, DEAD_BUSH_ID, FERN_ID, LILAC_ID, LILY_OF_THE_VALLEY_ID, MELON_ID, ORANGE_TULIP_ID,
  OXEYE_DAISY_ID, PEONY_ID, PINK_TULIP_ID, POPPY_ID, PUMPKIN_ID, RED_MUSHROOM_ID, RED_TULIP_ID,
  ROSE_BUSH_ID, SUNFLOWER_ID, SWEET_BERRY_BUSH_ID, TALL_GRASS_ID, WHITE_TULIP_ID,
} from '../blocks';

export enum Biome {
  Ocean, DeepOcean, WarmOcean, LukewarmOcean, DeepLukewarmOcean, ColdOcean, DeepColdOcean,
  FrozenOcean, DeepFrozenOcean, MushroomFields,
  River, FrozenRiver, Beach, SnowyBeach, StonyShore,
  Plains, SunflowerPlains, SnowyPlains, IceSpikes, Desert, Swamp, MangroveSwamp,
  Forest, FlowerForest, BirchForest, OldGrowthBirchForest, DarkForest, PaleGarden,
  Taiga, SnowyTaiga, OldGrowthPineTaiga, OldGrowthSpruceTaiga,
  Jungle, SparseJungle, BambooJungle,
  Savanna, SavannaPlateau, WindsweptSavanna,
  WindsweptHills, WindsweptGravellyHills, WindsweptForest,
  Meadow, CherryGrove, Grove, SnowySlopes, JaggedPeaks, FrozenPeaks, StonyPeaks,
  Badlands, WoodedBadlands, ErodedBadlands,
  DripstoneCaves, LushCaves, DeepDark,
}

/** Tree generators (see features.ts). */
export const enum Tree {
  Oak, FancyOak, SwampOak, Birch, TallBirch, Spruce, Pine, MegaSpruce, MegaPine,
  Jungle, MegaJungle, JungleBush, Acacia, DarkOak, PaleOak, Cherry, Mangrove,
  HugeRedMushroom, HugeBrownMushroom, Cactus,
}

/** Surface rule families (see TerrainGenerator.surfaceTop). */
export const enum Surf {
  Grass, Sand, Stone, Gravel, SnowyGrass, Snow, Mycelium, Mud, Podzol, Badlands, WoodedBadlands,
  WindsweptHills, GravellyHills, StonyPeaks, FrozenPeaks, JaggedPeaks, Grove, StonyShore, Swamp,
}

/** Sea-floor material. */
export const enum Bed { Gravel, Sand, Dirt }

export interface BiomeDef {
  name: string;
  /** Vanilla colours, 0xRRGGBB (sRGB). */
  grass: number;
  foliage: number;
  water: number;
  surface: Surf;
  bed: Bed;
  /** Water surface freezes and precipitation is snow. */
  frozen: boolean;
  /** Tree attempts per chunk (vanilla feature count) and weighted species. */
  trees: number;
  treeKinds: ReadonlyArray<readonly [Tree, number]>;
  /** Small plants: [block id, chance per surface column]. */
  flora: ReadonlyArray<readonly [number, number]>;
}

const WATER = 0x44aff5;
type Kinds = ReadonlyArray<readonly [Tree, number]>;
type Flora = ReadonlyArray<readonly [number, number]>;

const GRASSY: Flora = [[TALL_GRASS_ID, 0.12], [DANDELION_ID, 0.004], [POPPY_ID, 0.004]];
const PLAINS_FLORA: Flora = [
  [TALL_GRASS_ID, 0.3], [DANDELION_ID, 0.006], [POPPY_ID, 0.005], [AZURE_BLUET_ID, 0.004],
  [OXEYE_DAISY_ID, 0.004], [CORNFLOWER_ID, 0.003], [RED_TULIP_ID, 0.001], [PUMPKIN_ID, 0.0002],
];
const FOREST_FLORA: Flora = [
  [TALL_GRASS_ID, 0.1], [DANDELION_ID, 0.004], [POPPY_ID, 0.004], [LILY_OF_THE_VALLEY_ID, 0.002],
  [LILAC_ID, 0.002], [ROSE_BUSH_ID, 0.002], [PEONY_ID, 0.002], [BROWN_MUSHROOM_ID, 0.001],
];
const FLOWER_FOREST_FLORA: Flora = [
  [TALL_GRASS_ID, 0.1], [DANDELION_ID, 0.02], [POPPY_ID, 0.02], [ALLIUM_ID, 0.02], [AZURE_BLUET_ID, 0.02],
  [RED_TULIP_ID, 0.015], [ORANGE_TULIP_ID, 0.015], [WHITE_TULIP_ID, 0.015], [PINK_TULIP_ID, 0.015],
  [OXEYE_DAISY_ID, 0.02], [CORNFLOWER_ID, 0.02], [LILY_OF_THE_VALLEY_ID, 0.015],
  [LILAC_ID, 0.01], [ROSE_BUSH_ID, 0.01], [PEONY_ID, 0.01],
];
const TAIGA_FLORA: Flora = [
  [FERN_ID, 0.12], [TALL_GRASS_ID, 0.05], [SWEET_BERRY_BUSH_ID, 0.008],
  [BROWN_MUSHROOM_ID, 0.003], [RED_MUSHROOM_ID, 0.002],
];
const OLD_TAIGA_FLORA: Flora = [
  [FERN_ID, 0.2], [TALL_GRASS_ID, 0.04], [BROWN_MUSHROOM_ID, 0.012], [RED_MUSHROOM_ID, 0.006],
  [SWEET_BERRY_BUSH_ID, 0.006], [DEAD_BUSH_ID, 0.004],
];
const JUNGLE_FLORA: Flora = [
  [TALL_GRASS_ID, 0.25], [FERN_ID, 0.12], [MELON_ID, 0.002], [DANDELION_ID, 0.003], [POPPY_ID, 0.003],
];
const SAVANNA_FLORA: Flora = [[TALL_GRASS_ID, 0.4], [DANDELION_ID, 0.002]];
const SWAMP_FLORA: Flora = [
  [TALL_GRASS_ID, 0.15], [BLUE_ORCHID_ID, 0.006], [BROWN_MUSHROOM_ID, 0.006], [RED_MUSHROOM_ID, 0.004],
  [DEAD_BUSH_ID, 0.003], [FERN_ID, 0.02],
];
const MEADOW_FLORA: Flora = [
  [TALL_GRASS_ID, 0.35], [DANDELION_ID, 0.03], [CORNFLOWER_ID, 0.03], [ALLIUM_ID, 0.02],
  [AZURE_BLUET_ID, 0.03], [OXEYE_DAISY_ID, 0.03], [POPPY_ID, 0.03],
];
const DESERT_FLORA: Flora = [[DEAD_BUSH_ID, 0.008]];
const BADLANDS_FLORA: Flora = [[DEAD_BUSH_ID, 0.03]];
const MUSHROOM_FLORA: Flora = [[RED_MUSHROOM_ID, 0.02], [BROWN_MUSHROOM_ID, 0.03]];
const NONE: Flora = [];

const OAKS: Kinds = [[Tree.Oak, 4], [Tree.FancyOak, 1]];
const TAIGA_TREES: Kinds = [[Tree.Spruce, 2], [Tree.Pine, 1]];
const NO_TREES: Kinds = [];

function b(
  name: string, grass: number, foliage: number, water: number, surface: Surf,
  trees: number, treeKinds: Kinds, flora: Flora, extra: Partial<BiomeDef> = {}
): BiomeDef {
  return { name, grass, foliage, water, surface, bed: Bed.Gravel, frozen: false, trees, treeKinds, flora, ...extra };
}

const ocean = (name: string, water: number, bed: Bed, frozen = false): BiomeDef =>
  b(name, 0x8eb971, 0x71a74d, water, Surf.Grass, 0, NO_TREES, NONE, { bed, frozen });

export const BIOMES: Record<Biome, BiomeDef> = {
  [Biome.Ocean]: ocean('Ocean', 0x1787d4, Bed.Gravel),
  [Biome.DeepOcean]: ocean('Deep Ocean', 0x1787d4, Bed.Gravel),
  [Biome.WarmOcean]: ocean('Warm Ocean', 0x02b0e5, Bed.Sand),
  [Biome.LukewarmOcean]: ocean('Lukewarm Ocean', 0x0d96db, Bed.Sand),
  [Biome.DeepLukewarmOcean]: ocean('Deep Lukewarm Ocean', 0x0d96db, Bed.Sand),
  [Biome.ColdOcean]: ocean('Cold Ocean', 0x2080c9, Bed.Gravel),
  [Biome.DeepColdOcean]: ocean('Deep Cold Ocean', 0x2080c9, Bed.Gravel),
  [Biome.FrozenOcean]: ocean('Frozen Ocean', 0x2570b5, Bed.Gravel, true),
  [Biome.DeepFrozenOcean]: ocean('Deep Frozen Ocean', 0x2570b5, Bed.Gravel, true),
  [Biome.MushroomFields]: b('Mushroom Fields', 0x55c93f, 0x2bbb0f, 0x8a8997, Surf.Mycelium, 0.6,
    [[Tree.HugeRedMushroom, 1], [Tree.HugeBrownMushroom, 1]], MUSHROOM_FLORA),

  [Biome.River]: b('River', 0x8eb971, 0x71a74d, 0x0084ff, Surf.Grass, 0, NO_TREES, GRASSY, { bed: Bed.Dirt }),
  [Biome.FrozenRiver]: b('Frozen River', 0x80b497, 0x60a17b, 0x185390, Surf.SnowyGrass, 0, NO_TREES, NONE,
    { bed: Bed.Dirt, frozen: true }),
  [Biome.Beach]: b('Beach', 0x91bd59, 0x77ab2f, 0x157cab, Surf.Sand, 0, NO_TREES, NONE, { bed: Bed.Sand }),
  [Biome.SnowyBeach]: b('Snowy Beach', 0x83b593, 0x64a278, 0x1463a5, Surf.Sand, 0, NO_TREES, NONE,
    { bed: Bed.Sand, frozen: true }),
  [Biome.StonyShore]: b('Stony Shore', 0x8ab689, 0x6da36b, 0x0d67bb, Surf.StonyShore, 0, NO_TREES, NONE),

  [Biome.Plains]: b('Plains', 0x91bd59, 0x77ab2f, WATER, Surf.Grass, 0.05, [[Tree.Oak, 2], [Tree.FancyOak, 1]], PLAINS_FLORA),
  [Biome.SunflowerPlains]: b('Sunflower Plains', 0x91bd59, 0x77ab2f, WATER, Surf.Grass, 0.05, OAKS,
    [[SUNFLOWER_ID, 0.04], ...PLAINS_FLORA]),
  [Biome.SnowyPlains]: b('Snowy Plains', 0x80b497, 0x60a17b, 0x14559b, Surf.SnowyGrass, 0.1, [[Tree.Spruce, 1]],
    [[TALL_GRASS_ID, 0.01]], { frozen: true }),
  [Biome.IceSpikes]: b('Ice Spikes', 0x80b497, 0x60a17b, 0x14559b, Surf.Snow, 0, NO_TREES, NONE, { frozen: true }),
  [Biome.Desert]: b('Desert', 0xbfb755, 0xaea42a, 0x32a598, Surf.Sand, 0.8, [[Tree.Cactus, 1]], DESERT_FLORA, { bed: Bed.Sand }),
  [Biome.Swamp]: b('Swamp', 0x6a7039, 0x6a7039, 0x617b64, Surf.Swamp, 2, [[Tree.SwampOak, 1]], SWAMP_FLORA, { bed: Bed.Dirt }),
  [Biome.MangroveSwamp]: b('Mangrove Swamp', 0x6a7039, 0x8db127, 0x3a7a6a, Surf.Mud, 12, [[Tree.Mangrove, 1]],
    [[TALL_GRASS_ID, 0.05]], { bed: Bed.Dirt }),

  [Biome.Forest]: b('Forest', 0x79c05a, 0x59ae30, 0x1e97f2, Surf.Grass, 10,
    [[Tree.Oak, 7], [Tree.Birch, 2], [Tree.FancyOak, 1]], FOREST_FLORA),
  [Biome.FlowerForest]: b('Flower Forest', 0x79c05a, 0x59ae30, 0x20a3cc, Surf.Grass, 6,
    [[Tree.Oak, 4], [Tree.Birch, 3], [Tree.FancyOak, 1]], FLOWER_FOREST_FLORA),
  [Biome.BirchForest]: b('Birch Forest', 0x88bb67, 0x6ba941, 0x0677ce, Surf.Grass, 10, [[Tree.Birch, 1]], FOREST_FLORA),
  [Biome.OldGrowthBirchForest]: b('Old Growth Birch Forest', 0x88bb67, 0x6ba941, WATER, Surf.Grass, 10,
    [[Tree.TallBirch, 1]], FOREST_FLORA),
  [Biome.DarkForest]: b('Dark Forest', 0x507a32, 0x59ae30, 0x3b6cd1, Surf.Grass, 20,
    [[Tree.DarkOak, 13], [Tree.Oak, 3], [Tree.Birch, 2], [Tree.HugeRedMushroom, 1], [Tree.HugeBrownMushroom, 1]],
    [[TALL_GRASS_ID, 0.06], [BROWN_MUSHROOM_ID, 0.01], [RED_MUSHROOM_ID, 0.006], [ROSE_BUSH_ID, 0.003], [PEONY_ID, 0.003]]),
  [Biome.PaleGarden]: b('Pale Garden', 0x778272, 0x878d76, 0x76889d, Surf.Grass, 20, [[Tree.PaleOak, 1]],
    [[TALL_GRASS_ID, 0.05]]),

  [Biome.Taiga]: b('Taiga', 0x86b783, 0x68a464, 0x287082, Surf.Grass, 10, TAIGA_TREES, TAIGA_FLORA),
  [Biome.SnowyTaiga]: b('Snowy Taiga', 0x80b497, 0x60a17b, 0x205e83, Surf.SnowyGrass, 10, TAIGA_TREES,
    [[FERN_ID, 0.04], [SWEET_BERRY_BUSH_ID, 0.004]], { frozen: true }),
  [Biome.OldGrowthPineTaiga]: b('Old Growth Pine Taiga', 0x86b87f, 0x68a55f, 0x2d6d77, Surf.Podzol, 10,
    [[Tree.MegaPine, 4], [Tree.MegaSpruce, 1], [Tree.Spruce, 4], [Tree.Pine, 4]], OLD_TAIGA_FLORA),
  [Biome.OldGrowthSpruceTaiga]: b('Old Growth Spruce Taiga', 0x86b783, 0x68a464, WATER, Surf.Podzol, 10,
    [[Tree.MegaSpruce, 4], [Tree.MegaPine, 1], [Tree.Spruce, 5], [Tree.Pine, 3]], OLD_TAIGA_FLORA),

  [Biome.Jungle]: b('Jungle', 0x59c93c, 0x30bb0b, 0x14a2c5, Surf.Grass, 30,
    [[Tree.Jungle, 5], [Tree.JungleBush, 5], [Tree.MegaJungle, 2], [Tree.FancyOak, 1]], JUNGLE_FLORA),
  [Biome.SparseJungle]: b('Sparse Jungle', 0x64c73f, 0x3eb80f, 0x0d8ae3, Surf.Grass, 2,
    [[Tree.Jungle, 3], [Tree.JungleBush, 4], [Tree.FancyOak, 1]], JUNGLE_FLORA),
  [Biome.BambooJungle]: b('Bamboo Jungle', 0x59c93c, 0x30bb0b, 0x14a2c5, Surf.Podzol, 12,
    [[Tree.MegaJungle, 2], [Tree.JungleBush, 5], [Tree.FancyOak, 1]], [[BAMBOO_ID, 0.22], ...JUNGLE_FLORA]),

  [Biome.Savanna]: b('Savanna', 0xbfb755, 0xaea42a, 0x2c8b9c, Surf.Grass, 1.2, [[Tree.Acacia, 4], [Tree.Oak, 1]], SAVANNA_FLORA),
  [Biome.SavannaPlateau]: b('Savanna Plateau', 0xbfb755, 0xaea42a, 0x2590a8, Surf.Grass, 1.2,
    [[Tree.Acacia, 4], [Tree.Oak, 1]], SAVANNA_FLORA),
  [Biome.WindsweptSavanna]: b('Windswept Savanna', 0xbfb755, 0xaea42a, 0x2590a8, Surf.WindsweptHills, 2,
    [[Tree.Acacia, 4], [Tree.Oak, 1]], SAVANNA_FLORA),

  [Biome.WindsweptHills]: b('Windswept Hills', 0x8ab689, 0x6da36b, 0x007bf7, Surf.WindsweptHills, 0.4,
    [[Tree.Spruce, 2], [Tree.Oak, 1]], GRASSY),
  [Biome.WindsweptGravellyHills]: b('Windswept Gravelly Hills', 0x8ab689, 0x6da36b, 0x0e63ab, Surf.GravellyHills, 0.2,
    [[Tree.Spruce, 2], [Tree.Oak, 1]], [[TALL_GRASS_ID, 0.02]]),
  [Biome.WindsweptForest]: b('Windswept Forest', 0x8ab689, 0x6da36b, 0x0e63ab, Surf.Grass, 3,
    [[Tree.Spruce, 2], [Tree.Oak, 1]], GRASSY),

  [Biome.Meadow]: b('Meadow', 0x83bb6d, 0x63a948, WATER, Surf.Grass, 0.03,
    [[Tree.FancyOak, 1], [Tree.Birch, 1]], MEADOW_FLORA),
  [Biome.CherryGrove]: b('Cherry Grove', 0xb6db61, 0xb6db61, 0x5db7ef, Surf.Grass, 3, [[Tree.Cherry, 1]],
    [[TALL_GRASS_ID, 0.15]]),
  [Biome.Grove]: b('Grove', 0x80b497, 0x60a17b, WATER, Surf.Grove, 10, TAIGA_TREES, NONE, { frozen: true }),
  [Biome.SnowySlopes]: b('Snowy Slopes', 0x80b497, 0x60a17b, WATER, Surf.Snow, 0, NO_TREES, NONE, { frozen: true }),
  [Biome.JaggedPeaks]: b('Jagged Peaks', 0x80b497, 0x60a17b, WATER, Surf.JaggedPeaks, 0, NO_TREES, NONE, { frozen: true }),
  [Biome.FrozenPeaks]: b('Frozen Peaks', 0x80b497, 0x60a17b, WATER, Surf.FrozenPeaks, 0, NO_TREES, NONE, { frozen: true }),
  [Biome.StonyPeaks]: b('Stony Peaks', 0x9abe4b, 0x82ac1e, WATER, Surf.StonyPeaks, 0, NO_TREES, NONE),

  [Biome.Badlands]: b('Badlands', 0x90814d, 0x9e814d, 0x4e7f81, Surf.Badlands, 0.3, [[Tree.Cactus, 1]], BADLANDS_FLORA, { bed: Bed.Sand }),
  [Biome.WoodedBadlands]: b('Wooded Badlands', 0x90814d, 0x9e814d, 0x55809e, Surf.WoodedBadlands, 5, [[Tree.Oak, 1]],
    [[TALL_GRASS_ID, 0.1], [DEAD_BUSH_ID, 0.01]], { bed: Bed.Sand }),
  [Biome.ErodedBadlands]: b('Eroded Badlands', 0x90814d, 0x9e814d, 0x497f99, Surf.Badlands, 0.2, [[Tree.Cactus, 1]],
    BADLANDS_FLORA, { bed: Bed.Sand }),

  [Biome.DripstoneCaves]: b('Dripstone Caves', 0x91bd59, 0x77ab2f, WATER, Surf.Grass, 0, NO_TREES, NONE),
  [Biome.LushCaves]: b('Lush Caves', 0x91bd59, 0x77ab2f, WATER, Surf.Grass, 0, NO_TREES, NONE),
  [Biome.DeepDark]: b('Deep Dark', 0x91bd59, 0x77ab2f, WATER, Surf.Grass, 0, NO_TREES, NONE),
};

export const BIOME_COUNT = Object.keys(BIOMES).length;
export const BIOME_NAMES: readonly string[] = Array.from({ length: BIOME_COUNT }, (_, i) => BIOMES[i as Biome].name);

/** Flat lookup tables by biome id (hot paths). */
export const BIOME_LIST: readonly BiomeDef[] = Array.from({ length: BIOME_COUNT }, (_, i) => BIOMES[i as Biome]);

// --- climate -> biome (vanilla OverworldBiomeBuilder) --------------------------

const B = Biome;
const N = -1; // "no variant"

const OCEANS = [
  [B.DeepFrozenOcean, B.DeepColdOcean, B.DeepOcean, B.DeepLukewarmOcean, B.WarmOcean],
  [B.FrozenOcean, B.ColdOcean, B.Ocean, B.LukewarmOcean, B.WarmOcean],
];
const MIDDLE = [
  [B.SnowyPlains, B.SnowyPlains, B.SnowyPlains, B.SnowyTaiga, B.Taiga],
  [B.Plains, B.Plains, B.Forest, B.Taiga, B.OldGrowthSpruceTaiga],
  [B.FlowerForest, B.Plains, B.Forest, B.BirchForest, B.DarkForest],
  [B.Savanna, B.Savanna, B.Forest, B.Jungle, B.Jungle],
  [B.Desert, B.Desert, B.Desert, B.Desert, B.Desert],
];
const MIDDLE_VARIANT = [
  [B.IceSpikes, N, B.SnowyTaiga, N, N],
  [N, N, N, N, B.OldGrowthPineTaiga],
  [B.SunflowerPlains, N, N, B.OldGrowthBirchForest, N],
  [N, N, B.Plains, B.SparseJungle, B.BambooJungle],
  [N, N, N, N, N],
];
const PLATEAU = [
  [B.SnowyPlains, B.SnowyPlains, B.SnowyPlains, B.SnowyTaiga, B.SnowyTaiga],
  [B.Meadow, B.Meadow, B.Forest, B.Taiga, B.OldGrowthSpruceTaiga],
  [B.Meadow, B.Meadow, B.Meadow, B.Meadow, B.PaleGarden],
  [B.SavannaPlateau, B.SavannaPlateau, B.Forest, B.Forest, B.Jungle],
  [B.Badlands, B.Badlands, B.Badlands, B.WoodedBadlands, B.WoodedBadlands],
];
const PLATEAU_VARIANT = [
  [B.IceSpikes, N, N, N, N],
  [B.CherryGrove, N, B.Meadow, B.Meadow, B.OldGrowthPineTaiga],
  [B.CherryGrove, B.CherryGrove, B.Forest, B.BirchForest, N],
  [N, N, N, N, N],
  [B.ErodedBadlands, B.ErodedBadlands, N, N, N],
];
const SHATTERED = [
  [B.WindsweptGravellyHills, B.WindsweptGravellyHills, B.WindsweptHills, B.WindsweptForest, B.WindsweptForest],
  [B.WindsweptGravellyHills, B.WindsweptGravellyHills, B.WindsweptHills, B.WindsweptForest, B.WindsweptForest],
  [B.WindsweptHills, B.WindsweptHills, B.WindsweptHills, B.WindsweptForest, B.WindsweptForest],
  [N, N, N, N, N],
  [N, N, N, N, N],
];

const pickMiddle = (t: number, h: number, w: number): Biome => {
  if (w < 0) return MIDDLE[t][h];
  const v = MIDDLE_VARIANT[t][h];
  return v === N ? MIDDLE[t][h] : v;
};
const pickBadlands = (h: number, w: number): Biome =>
  h < 2 ? (w < 0 ? B.Badlands : B.ErodedBadlands) : h < 3 ? B.Badlands : B.WoodedBadlands;
const pickMiddleOrBadlands = (t: number, h: number, w: number): Biome =>
  t === 4 ? pickBadlands(h, w) : pickMiddle(t, h, w);
const pickPlateau = (t: number, h: number, w: number): Biome => {
  if (w >= 0) {
    const v = PLATEAU_VARIANT[t][h];
    if (v !== N) return v;
  }
  return PLATEAU[t][h];
};
const pickSlope = (t: number, h: number, w: number): Biome =>
  t >= 3 ? pickPlateau(t, h, w) : h <= 1 ? B.SnowySlopes : B.Grove;
const pickMiddleOrBadlandsOrSlope = (t: number, h: number, w: number): Biome =>
  t === 0 ? pickSlope(t, h, w) : pickMiddleOrBadlands(t, h, w);
const pickPeak = (t: number, h: number, w: number): Biome =>
  t <= 2 ? (w < 0 ? B.JaggedPeaks : B.FrozenPeaks) : t === 3 ? B.StonyPeaks : pickBadlands(h, w);
const pickBeach = (t: number): Biome => (t === 0 ? B.SnowyBeach : t === 4 ? B.Desert : B.Beach);
const windsweptSavannaOr = (t: number, h: number, w: number, other: Biome): Biome =>
  t > 1 && h < 4 && w >= 0 ? B.WindsweptSavanna : other;
const pickShattered = (t: number, h: number, w: number): Biome => {
  const v = SHATTERED[t][h];
  return v === N ? pickMiddle(t, h, w) : v;
};
const pickShatteredCoast = (t: number, h: number, w: number): Biome =>
  windsweptSavannaOr(t, h, w, w >= 0 ? pickMiddle(t, h, w) : pickBeach(t));
const pickSwamp = (t: number, h: number, w: number): Biome =>
  t === 0 ? pickMiddle(t, h, w) : t <= 2 ? B.Swamp : B.MangroveSwamp;

function index(v: number, cuts: readonly number[]): number {
  let i = 0;
  while (i < cuts.length && v >= cuts[i]) i++;
  return i;
}
const T_CUTS = [-0.45, -0.15, 0.2, 0.55];
const H_CUTS = [-0.35, -0.1, 0.1, 0.3];
const E_CUTS = [-0.78, -0.375, -0.2225, 0.05, 0.45, 0.55];
/** mushroom | deep ocean | ocean | coast | near inland | mid inland | far inland */
const C_CUTS = [-1.05, -0.455, -0.19, -0.11, 0.03, 0.3];
const COAST = 3, NEAR = 4, MID = 5;
/** Weirdness slice boundaries; slice kinds: 0 valley, 1 low, 2 mid, 3 high, 4 peaks. */
const W_CUTS = [-0.9333, -0.7667, -0.5667, -0.4, -0.2667, -0.05, 0.05, 0.2667, 0.4, 0.5667, 0.7667, 0.9333];
const W_SLICE = [2, 3, 4, 3, 2, 1, 0, 1, 2, 3, 4, 3, 2];

export const temperatureIndex = (t: number): number => index(t, T_CUTS);

/** Surface biome for a climate point (vanilla parameter space). */
export function pickBiome(temp: number, humid: number, cont: number, erosion: number, weird: number): Biome {
  const t = index(temp, T_CUTS);
  const c = index(cont, C_CUTS);
  if (c === 0) return B.MushroomFields;
  if (c === 1) return OCEANS[0][t];
  if (c === 2) return OCEANS[1][t];
  const h = index(humid, H_CUTS);
  const e = index(erosion, E_CUTS);
  const w = weird;
  const inland = c > NEAR; // mid or far inland

  switch (W_SLICE[index(weird, W_CUTS)]) {
    case 0: // valleys
      if (c === COAST) {
        if (e <= 1) return w < 0 ? B.StonyShore : t === 0 ? B.FrozenRiver : B.River;
        return t === 0 ? B.FrozenRiver : B.River;
      }
      if (e <= 1) return c === NEAR ? (t === 0 ? B.FrozenRiver : B.River) : pickMiddleOrBadlands(t, h, w);
      if (e <= 5) return t === 0 ? B.FrozenRiver : B.River;
      return t === 0 ? B.FrozenRiver : t <= 2 ? B.Swamp : B.MangroveSwamp;

    case 1: // low
      if (c === COAST) {
        if (e <= 2) return B.StonyShore;
        if (e <= 4) return pickBeach(t);
        if (e === 5) return pickShatteredCoast(t, h, w);
        return pickBeach(t);
      }
      if (e <= 1) return c === NEAR ? pickMiddleOrBadlands(t, h, w) : pickMiddleOrBadlandsOrSlope(t, h, w);
      if (e <= 3) return c === NEAR ? pickMiddle(t, h, w) : pickMiddleOrBadlands(t, h, w);
      if (e === 4) return pickMiddle(t, h, w);
      if (e === 5) return c === NEAR ? windsweptSavannaOr(t, h, w, pickMiddle(t, h, w)) : pickMiddle(t, h, w);
      return pickSwamp(t, h, w);

    case 2: // mid
      if (c === COAST) {
        if (e <= 2) return B.StonyShore;
        if (e === 3) return pickMiddle(t, h, w);
        if (e === 4) return w < 0 ? pickBeach(t) : pickMiddle(t, h, w);
        if (e === 5) return pickShatteredCoast(t, h, w);
        return w < 0 ? pickBeach(t) : pickMiddle(t, h, w);
      }
      if (e === 0) return pickSlope(t, h, w);
      if (e === 1) return c <= MID ? pickMiddleOrBadlandsOrSlope(t, h, w) : t === 0 ? pickSlope(t, h, w) : pickPlateau(t, h, w);
      if (e === 2) return c === NEAR ? pickMiddle(t, h, w) : c === MID ? pickMiddleOrBadlands(t, h, w) : pickPlateau(t, h, w);
      if (e === 3) return c === NEAR ? pickMiddle(t, h, w) : pickMiddleOrBadlands(t, h, w);
      if (e === 4) return pickMiddle(t, h, w);
      if (e === 5) return c === NEAR ? windsweptSavannaOr(t, h, w, pickMiddle(t, h, w)) : pickShattered(t, h, w);
      return pickSwamp(t, h, w);

    case 3: // high
      if (c === COAST) {
        if (e <= 4) return pickMiddle(t, h, w);
        if (e === 5) return windsweptSavannaOr(t, h, w, pickShattered(t, h, w));
        return pickMiddle(t, h, w);
      }
      if (e === 0) return inland ? pickPeak(t, h, w) : pickSlope(t, h, w);
      if (e === 1) return inland ? pickSlope(t, h, w) : pickMiddleOrBadlandsOrSlope(t, h, w);
      if (e === 2) return inland ? pickPlateau(t, h, w) : pickMiddle(t, h, w);
      if (e === 3) return c === NEAR ? pickMiddle(t, h, w) : c === MID ? pickMiddleOrBadlands(t, h, w) : pickPlateau(t, h, w);
      if (e === 4) return pickMiddle(t, h, w);
      if (e === 5) return inland ? pickShattered(t, h, w) : windsweptSavannaOr(t, h, w, pickShattered(t, h, w));
      return pickMiddle(t, h, w);

    default: // peaks
      if (e === 0) return pickPeak(t, h, w);
      if (e === 1) return inland ? pickPeak(t, h, w) : pickMiddleOrBadlandsOrSlope(t, h, w);
      if (e === 2) return inland ? pickPlateau(t, h, w) : pickMiddle(t, h, w);
      if (e === 3) return c <= NEAR ? pickMiddle(t, h, w) : c === MID ? pickMiddleOrBadlands(t, h, w) : pickPlateau(t, h, w);
      if (e === 4) return pickMiddle(t, h, w);
      if (e === 5) return inland ? pickShattered(t, h, w) : windsweptSavannaOr(t, h, w, pickShattered(t, h, w));
      return pickMiddle(t, h, w);
  }
}

/** Climate cut-offs of the cave biomes (see pickCaveBiome). */
export const LUSH_HUMID = 0.34;
export const DRIPSTONE_CONT = 0.52;
export const DEEP_DARK_EROSION = -0.42;

/**
 * Cave biome at `depth` blocks below the surface, or -1 for none. Vanilla
 * keys these on depth plus one climate extreme each; the cut-offs are scaled
 * to this generator's parameter spread so the caves stay findable.
 */
export function pickCaveBiome(humid: number, cont: number, erosion: number, depth: number, y: number, deepY: number): number {
  if (depth < 10) return -1;
  if (erosion < DEEP_DARK_EROSION && y < deepY) return B.DeepDark;
  if (humid > LUSH_HUMID) return B.LushCaves;
  if (cont > DRIPSTONE_CONT) return B.DripstoneCaves;
  return -1;
}
